import { createContext, useContext, useState, useCallback, useRef } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

export const ToastContext = createContext(null);

const TOAST_VARIANTS = {
  success: { icon: CheckCircle2, classes: 'bg-emerald-50 text-emerald-900 ring-emerald-200' },
  error:   { icon: AlertCircle,  classes: 'bg-red-50 text-red-900 ring-red-200' },
  info:    { icon: Info,         classes: 'bg-sky-50 text-sky-900 ring-sky-200' },
};

const DEFAULT_DURATION = 4000;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (message, { variant = 'info', duration = DEFAULT_DURATION, onClick } = {}) => {
      const id = crypto.randomUUID();
      setToasts((prev) => [...prev, { id, message, variant, onClick }]);

      if (duration > 0) {
        const timer = setTimeout(() => dismiss(id), duration);
        timersRef.current.set(id, timer);
      }
      return id;
    },
    [dismiss]
  );

  // Helpers semánticos — más legibles en el call-site
  const api = {
    show,
    dismiss,
    success: (msg, opts) => show(msg, { ...opts, variant: 'success' }),
    error:   (msg, opts) => show(msg, { ...opts, variant: 'error' }),
    info:    (msg, opts) => show(msg, { ...opts, variant: 'info' }),
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

const ToastViewport = ({ toasts, onDismiss }) => {
  return (
    <div
      role="region"
      aria-label="Notificaciones"
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex flex-col items-center gap-2 px-4 pt-safe"
    >
      <div className="flex w-full max-w-md flex-col gap-2 pt-4">
        {toasts.map((toast) => {
          const { icon: Icon, classes } = TOAST_VARIANTS[toast.variant];
          const isClickable = typeof toast.onClick === 'function';

          // Si el toast es clickeable, al tocarlo se ejecuta onClick y se cierra.
          // El botón X mantiene stopPropagation para cerrar sin disparar la acción.
          const handleActivate = () => {
            toast.onClick();
            onDismiss(toast.id);
          };

          return (
            <div
              key={toast.id}
              role="status"
              tabIndex={isClickable ? 0 : undefined}
              onClick={isClickable ? handleActivate : undefined}
              onKeyDown={
                isClickable
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleActivate();
                      }
                    }
                  : undefined
              }
              className={[
                'pointer-events-auto flex items-start gap-3 rounded-xl px-4 py-3 shadow-lg ring-1',
                'animate-slide-up',
                classes,
                isClickable
                  ? 'cursor-pointer transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-offset-1'
                  : '',
              ].join(' ')}
            >
              <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <p className="flex-1 text-sm font-medium">{toast.message}</p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDismiss(toast.id);
                }}
                aria-label="Cerrar notificación"
                className="shrink-0 rounded p-0.5 hover:bg-black/5"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
};